import _ from "lodash";
import dayjs, { isDayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  ArrayParam,
  NumberParam,
  ObjectParam,
  encodeQueryParams,
  objectToSearchString,
  useQueryParams,
  withDefault,
} from "use-query-params";

import { SORTER_KEYS } from "constants/table";

dayjs.extend(utc);

export const encodeQueryParamsWithProducts = (
  selectedProductIds,
  paramConfigs = {},
  paramValues = {}
) => {
  return objectToSearchString(
    encodeQueryParams(
      { ...paramConfigs, products: NumberArrayParam },
      { ...paramValues, products: selectedProductIds }
    )
  );
};

export const useQueryParamsWithProducts = (
  selectedProductIds,
  queryParamConfigs = {}
) => {
  return useQueryParams({
    ...queryParamConfigs,
    products: withDefault(NumberArrayParam, selectedProductIds),
  });
};

export const NumberArrayParam = {
  encode(value) {
    return ArrayParam.encode(value);
  },
  decode(strValue) {
    return ArrayParam.decode(strValue)
      ?.map((val) => NumberParam.decode(val))
      .filter((val) => !isNaN(val));
  },
};

export const NumberRangeArrayParam = {
  encodeApi(value) {
    let results = value;
    // Number range must have 2 entries (start number, end number)
    if (results?.length !== 2 || (results[0] === null && results[1] === null)) {
      results = null;
    } else {
      results = [results[0] ?? "", results[1] ?? ""];
    }
    return results;
  },
};

export const DateRangeArrayParam = {
  encode(value) {
    let results = ArrayParam.encode(
      value?.map((val) => (!!val?.$isDayjsObject ? dayjs(val) : ""))
    );
    // Date range must have 2 entries (start date, end date)
    if (results?.length !== 2 || (results[0] === "" && results[1] === "")) {
      results = null;
    } else {
      results[0] = isDayjs(results[0])
        ? results[0].startOf("day").utc().format()
        : results[0];
      results[1] = isDayjs(results[1])
        ? results[1].endOf("day").utc().format()
        : results[1];
    }
    return results;
  },
  decode(strValue) {
    let results = ArrayParam.decode(strValue)?.map((val) =>
      !!val ? dayjs(val).utc(true).local() : null
    );
    // Date range must have 2 entries (start date, end date)
    if (results?.length !== 2) {
      results = null;
    }
    return results;
  },
};

export const SorterArrayParam = {
  encode(value) {
    const encodedValue = {};
    value
      ?.filter(
        (val) => !!val[SORTER_KEYS.SORT_BY] && !!val[SORTER_KEYS.SORT_ORDER]
      )
      .forEach((val) => {
        return Object.assign(encodedValue, {
          [_.camelCase(val[SORTER_KEYS.SORT_BY])]: val[SORTER_KEYS.SORT_ORDER],
        });
      });
    const results = !!value ? ObjectParam.encode(encodedValue) : null;
    return results;
  },
  encodeApi(value) {
    const encodedValue = value
      ?.filter(
        (val) => !!val?.[SORTER_KEYS.SORT_BY] && !!val?.[SORTER_KEYS.SORT_ORDER]
      )
      .map((val) =>
        [val[SORTER_KEYS.SORT_BY], val[SORTER_KEYS.SORT_ORDER]].join("-")
      );
    return encodedValue;
  },
  decode(strValue) {
    const decodedValue = ObjectParam.decode(strValue);
    const results = !!decodedValue
      ? Object.entries(decodedValue).map(([key, val]) => ({
          [SORTER_KEYS.SORT_BY]: _.snakeCase(key),
          [SORTER_KEYS.SORT_ORDER]: val,
        }))
      : null;
    return results;
  },
};
